import React, { Component } from "react";
import FaqSearchBar from "./FaqSearchBar";
import FaqAccordionItem from "./FaqAccordionItem";
import FaqAddModal from "./FaqAddModal";
import FaqService from "../../services/FaqService";
import UserService from "../../services/UserService";
import * as Icon from "react-feather";
import "./faq.css";

class FaqPage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            faqs: [],
            loading: true,
            searching: false,
            searchQuery: "",            // last submitted query, used for highlighting
            searchResults: null,
            fallbackMessage: null,
            expandedId: null,
            showAddModal: false,
        };
    }

    async componentDidMount() {
        await this.fetchFaqs();
    }

    fetchFaqs = async () => {
        this.setState({ loading: true });
        try {
            let faqs = await FaqService.fetchAll();
            this.setState({ faqs, loading: false });
        } catch (error) {
            console.log(error);
            this.setState({ loading: false });
        }
    };

    onSearch = async (query) => {
        this.setState({ searching: true, searchQuery: query || "", fallbackMessage: null });

        try {
            let data = await FaqService.search({ query, provider: "openai", model: "gpt-4.1-mini", topN: 5 });

            if (!data || !data.results || data.results.length === 0) {
                this.setState({
                    searching: false,
                    searchResults: null,
                    fallbackMessage: (data && data.fallbackMessage) || "No results found. Try rephrasing your question.",
                });
                return;
            }

            this.setState({
                searching: false,
                searchResults: data.results,
                fallbackMessage: data.fallbackMessage || null,
            });
        } catch (error) {
            console.log(error);
            this.setState({
                searching: false,
                fallbackMessage: "Search failed. Please try again.",
            });
        }
    };

    onClearSearch = () => {
        this.setState({
            searchResults: null,
            searchQuery: "",
            fallbackMessage: null,
        });
    };

    onOpenAddModal = () => {
        this.setState({ showAddModal: true });
    };

    onCloseAddModal = () => {
        this.setState({ showAddModal: false });
    };

    onFaqSaved = async () => {
        this.setState({ showAddModal: false });
        await this.fetchFaqs();
    };

    onToggleAccordion = (id) => {
        this.setState((prevState) => ({
            expandedId: prevState.expandedId === id ? null : id,
        }));
    };

    getOrderedFaqs() {
        let { faqs, searchResults } = this.state;

        if (!searchResults) {
            return { matched: faqs, unmatched: [] };
        }

        let resultMap = {};
        searchResults.forEach((r) => {
            resultMap[r.faqId] = r;
        });

        let matched = [];
        let unmatched = [];

        faqs.forEach((faq) => {
            if (resultMap[faq.id]) {
                matched.push(faq);
            } else {
                unmatched.push(faq);
            }
        });

        // Sort matched by score descending
        matched.sort((a, b) => {
            let scoreA = resultMap[a.id] ? resultMap[a.id].score : 0;
            let scoreB = resultMap[b.id] ? resultMap[b.id].score : 0;
            return scoreB - scoreA;
        });

        return { matched, unmatched, resultMap };
    }

    render() {
        let { loading, searching, fallbackMessage, expandedId, searchResults, showAddModal } = this.state;

        let { matched, unmatched, resultMap } = this.getOrderedFaqs();

        return (
            <>
            <div className="container">
                <div className="container-header">
                    <div className="container-header-title">
                        <h1 className="container-header-title__name">
                            <Icon.HelpCircle size={22} style={{ marginRight: 8 }} />
                            FAQ
                        </h1>
                        <p className="container-header-title__sub">Find answers to common questions with AI-powered search</p>
                    </div>
                    <div className="container-header-actions">
                        {UserService.isAdministrator() && (
                            <button className="faq-add-btn" onClick={this.onOpenAddModal}>
                                <Icon.Plus size={16} />
                                Add FAQ
                            </button>
                        )}
                    </div>
                </div>

                <FaqSearchBar
                    onSearch={this.onSearch}
                    onClearSearch={this.onClearSearch}
                    searching={searching}
                    hasSearchResults={!!searchResults}
                />

                {fallbackMessage && (
                    <div className="faq-fallback">
                        <Icon.Info size={16} />
                        <span>{fallbackMessage}</span>
                    </div>
                )}

                {loading ? (
                    <div className="container-loader">
                        <Icon.Loader size={36} className="faq-spin" />
                        <p>Loading FAQs...</p>
                    </div>
                ) : (
                    <div className="faq-list">
                        {matched.length === 0 && unmatched.length === 0 && (
                            <div className="container-empty">
                                <Icon.HelpCircle size={36} />
                                <p>No FAQs available yet.</p>
                            </div>
                        )}

                        {matched.map((faq) => (
                            <FaqAccordionItem
                                key={faq.id}
                                faq={faq}
                                searchResult={resultMap ? resultMap[faq.id] : null}
                                query={this.state.searchQuery}
                                isExpanded={expandedId === faq.id}
                                onToggle={this.onToggleAccordion}
                            />
                        ))}

                        {searchResults && unmatched.length > 0 && (
                            <>
                                <div className="faq-divider">
                                    <span className="faq-divider__text">Other FAQs</span>
                                </div>
                                {unmatched.map((faq) => (
                                    <FaqAccordionItem
                                        key={faq.id}
                                        faq={faq}
                                        searchResult={null}
                                        query={this.state.searchQuery}
                                        isExpanded={expandedId === faq.id}
                                        onToggle={this.onToggleAccordion}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            {showAddModal && (
                <FaqAddModal
                    onClose={this.onCloseAddModal}
                    onSaved={this.onFaqSaved}
                />
            )}
            </>
        );
    }
}

export default FaqPage;
